import {getApiConfig} from '../config/configProvider';
import {
  BOX_PICK_FAILURE_REASON_OTHER,
  BOX_PICK_FAILURE_REASONS,
  BOX_PICKER_FORCED_HOSTS,
  BOX_WEIGHT_DECIMALS,
  STOCK_CHECK,
} from '../constants/constants';

// The server's box-svc flag is the real switch; this lets the website hide
// the picker without a server change. Missing from CONFIG_JS = enabled.
// Forced on for dev/UAT (BOX_PICKER_FORCED_HOSTS) regardless of that flag.
export const isBoxPickerEnabled = () => {
  if (
    typeof window !== 'undefined' &&
    BOX_PICKER_FORCED_HOSTS.indexOf(window.location.hostname) > -1
  ) {
    return true;
  }
  const config = getApiConfig();
  return !(config && config.boxPicker === false);
};

export const isBoxLine = line =>
  !!(line && Array.isArray(line.boxNumbers) && line.boxNumbers.length > 0);

// Basket lists and counts skip lines that have no stock check yet. A box pick
// is saved with none (the boxes are the stock), so a box line always counts.
export const hasStockCheckOrIsBoxLine = line =>
  !!(line && (line.stockCheck || isBoxLine(line)));

// A pick the server refused comes back as a line with no uuid that was never
// saved: it only carries the refusal and a fresh list.
export const isTransientBoxLine = line => !!(line && !line.uuid && line.boxes);

export const hasPickerData = line => !!(line && line.boxes);

// `boxes` is response-only picker data and transient lines were never saved,
// so neither belongs in the stored basket.
export const withoutPickerData = basket => {
  if (!basket || !Array.isArray(basket.items)) {
    return basket;
  }
  const items = basket.items
    .filter(line => !isTransientBoxLine(line))
    .map(line => {
      if (!hasPickerData(line)) {
        return line;
      }
      const {boxes, ...rest} = line; // eslint-disable-line no-unused-vars
      return rest;
    });
  return Object.assign({}, basket, {items});
};

export const findPickerLine = (basket, itemId) => {
  if (!basket || !Array.isArray(basket.items)) {
    return null;
  }
  const lines = basket.items.filter(
    line => line && line.itemId === itemId && hasPickerData(line)
  );
  // Prefer the refusal: it carries the message and the freshest list.
  return lines.find(isTransientBoxLine) || lines[0] || null;
};

export const isBoxUnavailable = line =>
  !!(
    line &&
    line.stockCheck &&
    line.stockCheck.result === STOCK_CHECK.BOX_UNAVAILABLE
  );

export const formatKg = kg =>
  `${(Number(kg) || 0).toFixed(BOX_WEIGHT_DECIMALS)} kg`;

export const formatPounds = amount => `£${(Number(amount) || 0).toFixed(2)}`;

export const getBoxPrice = (weightKg, unitPrice) =>
  (Number(weightKg) || 0) * (Number(unitPrice) || 0);

export const sumBoxWeights = boxes =>
  (boxes || []).reduce((total, box) => total + (Number(box.weightKg) || 0), 0);

export const getRemainingBoxNumbers = (line, boxNumber) =>
  ((line && line.boxNumbers) || []).filter(number => number !== boxNumber);

// Per-box rows for a basket line: `boxDetails` once jj-basket-svc ships it,
// box numbers alone until then.
export const getLineBoxRows = line => {
  if (!isBoxLine(line)) {
    return [];
  }
  const details = Array.isArray(line.boxDetails) ? line.boxDetails : [];
  return line.boxNumbers.map(
    boxNumber =>
      details.find(detail => detail && detail.boxNumber === boxNumber) || {
        boxNumber,
      }
  );
};

export const getBoxPickFailureReason = message => {
  if (!message) {
    return BOX_PICK_FAILURE_REASON_OTHER;
  }
  const match = BOX_PICK_FAILURE_REASONS.find(item => item.match.test(message));
  return match ? match.reason : BOX_PICK_FAILURE_REASON_OTHER;
};

// Fields that make a line a box line. Response merges that copy a fixed list
// of fields must carry these too, or a box line turns back into a kg line.
export const pickBoxLineFields = line => ({
  boxNumbers: line && line.boxNumbers,
  boxDetails: line && line.boxDetails,
  holdExpiresAt: line && line.holdExpiresAt,
  promiseId: line && line.promiseId,
  priceEstimated: line && line.priceEstimated,
  // Without this, a fresh ledger-unavailable (or other server) message on a
  // box line is dropped during the partial merge in data.helper.js's
  // updateCurrentBasket, so BoxLine.js's notice can never refresh on the
  // basket page after a peek/pick.
  messages: line && line.messages,
});

const findProductInList = (list, itemId) =>
  Array.isArray(list)
    ? list.find(product => product && product.itemId === itemId)
    : undefined;

// The basket line has no product name, so look in what's already loaded.
export const findProductName = (state, itemId) => {
  if (!state || !itemId) {
    return null;
  }
  const {basketItems, productDetail, searchResults} = state;
  const basketLine =
    basketItems &&
    (findProductInList(basketItems.inStock, itemId) ||
      findProductInList(basketItems.outOfStock, itemId));
  if (basketLine && basketLine.product && basketLine.product.itemName) {
    return basketLine.product.itemName;
  }
  if (productDetail && productDetail.itemId === itemId) {
    return productDetail.itemName;
  }
  const searchProduct = findProductInList(
    searchResults && (searchResults.results || searchResults),
    itemId
  );
  return (searchProduct && searchProduct.itemName) || null;
};
